import type { RenditionSize } from '@shared/utils/photoRendition';
import { addRendition } from '@shared/utils/photoRendition';

/**
 * Single source of truth for guest site photo rendition sizes.
 * Used by both OptimizedEventGuestSitePhoto (runtime) and PreloadImages (SSR).
 *
 * LOW_RES is preloaded during SSR and rendered immediately.
 * HIGH_RES is loaded in the background after hydration, then crossfaded in.
 */
export const LOW_RES_RENDITION: RenditionSize = 'tiny';
export const HIGH_RES_RENDITION: RenditionSize = 'xlarge';

/**
 * Approximate pixel widths for each named rendition bucket, used as `w` descriptors when
 * building a `srcSet` candidate list (PRNT-4849).
 *
 * ASSUMPTION, not a verified backend contract: the photo service (see `photoRendition.ts`)
 * buckets renditions by name only (`?rendition=<name>`) — no pixel-width mapping is published
 * anywhere in this repo, its docs, or the photo service client. These widths are a reasonable
 * judgment call (roughly doubling per bucket, landing `xlarge` near a common desktop hero width
 * and `full` as a generous near-original ceiling), calibrated to what the photo service is
 * observed to return for each bucket. Revisit if the photo service ever publishes real
 * per-bucket dimensions.
 */
export const RENDITION_WIDTH_PX: Readonly<Partial<Record<RenditionSize, number>>> = {
  small: 400,
  medium: 800,
  large: 1200,
  xlarge: 1600,
  full: 2400,
};

/** Rendition buckets offered as `srcSet` candidates for the sharp/high-res photo layer. */
export const HIGH_RES_SRCSET_RENDITIONS: ReadonlyArray<RenditionSize> = ['medium', 'large', 'xlarge', 'full'];

/**
 * Builds a `srcSet` string (e.g. `"url?rendition=medium 800w, url?rendition=large 1200w, ..."`)
 * across `HIGH_RES_SRCSET_RENDITIONS` for the given photo URL.
 *
 * This is the single place that decides which candidates exist for the high-res layer, so the
 * on-page `<img>` (OptimizedEventGuestSitePhoto) and its internal cache-warming preloader
 * (useProgressiveImage) always offer the browser the exact same candidate list — see
 * useProgressiveImage.ts's `containerWidthPx` param for why that consistency matters.
 */
export const buildHighResSrcSet = (photoUrl: string): string =>
  HIGH_RES_SRCSET_RENDITIONS.map(renditionSize => `${addRendition({ url: photoUrl, renditionSize })} ${RENDITION_WIDTH_PX[renditionSize]}w`).join(', ');
